import type { Camera, Scene } from 'three';

import { BrightnessControl } from '../../managers/lighting-manager';
import type { LightingManager } from '../../managers/lighting-manager';
import type { CameraManager } from '../../managers/camera-manager';

export type ShowBrightnessControlParams = {
	show: boolean;
	scene: Scene | null;
	camera: Camera | null;
	domElement: HTMLElement | null;
	lightingManager: LightingManager | null;
	cameraManager: CameraManager | null;
	getBrightnessControl: () => BrightnessControl | null;
	setBrightnessControl: (control: BrightnessControl | null) => void;
};

/**
 * Show or hide the brightness control sphere in the 3D scene.
 * On first show, lazily instantiates the BrightnessControl and wires
 * interaction callbacks to disable/enable camera controls during drag.
 */
export const showBrightnessControlCfn = (params: ShowBrightnessControlParams): void => {
	const {
		show,
		scene,
		camera,
		domElement,
		lightingManager,
		cameraManager,
		getBrightnessControl,
		setBrightnessControl
	} = params;

	if (show) {
		let brightnessControl = getBrightnessControl();

		// Lazily create on first show
		if (!brightnessControl) {
			if (!scene || !camera || !domElement || !lightingManager) return;

			brightnessControl = new BrightnessControl({
				scene,
				camera,
				domElement,
				lightingManager
			});

			// Disable camera controls during brightness drag
			brightnessControl.setOnInteractionStart(() => {
				if (cameraManager?.controls) {
					cameraManager.controls.enabled = false;
				}
			});
			brightnessControl.setOnInteractionEnd(() => {
				if (cameraManager?.controls) {
					cameraManager.controls.enabled = true;
				}
			});

			setBrightnessControl(brightnessControl);
		}

		brightnessControl.show();
	} else {
		const brightnessControl = getBrightnessControl();
		brightnessControl?.hide();
	}
};
